
import { useEffect, useState } from 'react';
import type { CalendarEvent } from '../page';

interface EventDetailsModalProps {
  event: CalendarEvent;
  onClose: () => void;
  levelColors: Record<string, string>;
}

export default function EventDetailsModal({ event, onClose, levelColors }: EventDetailsModalProps) {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showCalendarMenu, setShowCalendarMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    if (start === end) {
      return startDate.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    return `${startDate.toLocaleDateString('en-US', { 
      month: 'long',
      day: 'numeric'
    })} - ${endDate.toLocaleDateString('en-US', { 
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })}`;
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'GC': return 'ri-global-line';
      case 'Division': return 'ri-earth-line';
      case 'Union': return 'ri-community-line';
      case 'National': return 'ri-flag-line';
      default: return 'ri-calendar-event-line';
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'GC': return 'bg-slate-800';
      case 'Division': return 'bg-blue-600';
      case 'Union': return 'bg-amber-600';
      case 'National': return 'bg-emerald-600';
      default: return 'bg-gray-600';
    }
  };

  const handleShare = (platform: string) => {
    const eventUrl = window.location.href;
    const text = `${event.title} - ${formatDateRange(event.startDate, event.endDate)}`;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(eventUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + eventUrl)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(event.title)}&body=${encodeURIComponent(text + '\n\n' + event.description + '\n\n' + eventUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(eventUrl).then(() => {
          setCopySuccess(true);
          setTimeout(() => {
            setCopySuccess(false);
            setShowShareMenu(false);
          }, 2000);
        });
        return;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
      setShowShareMenu(false);
    }
  };

  const formatDateForCalendar = (dateStr: string, timeStr?: string) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    if (timeStr) {
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':');
      let hour = parseInt(hours);
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
      return `${year}${month}${day}T${String(hour).padStart(2, '0')}${minutes}00`;
    }
    
    return `${year}${month}${day}`;
  };

  const handleAddToCalendar = (type: string) => {
    const startDate = formatDateForCalendar(event.startDate, event.time);
    const endDate = formatDateForCalendar(event.endDate, event.time);
    const title = encodeURIComponent(event.title);
    const description = encodeURIComponent(event.description);
    const location = encodeURIComponent(event.location || '');
    
    let calendarUrl = '';
    
    if (type === 'google') {
      const dates = event.time 
        ? `${startDate}/${endDate}`
        : `${startDate}/${endDate}`;
      calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${description}&location=${location}`;
      window.open(calendarUrl, '_blank');
    } else if (type === 'outlook') {
      calendarUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${startDate}&enddt=${endDate}&body=${description}&location=${location}`;
      window.open(calendarUrl, '_blank');
    } else if (type === 'ics') {
      const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${startDate.replace(/[-:]/g, '')}
DTEND:${endDate.replace(/[-:]/g, '')}
SUMMARY:${event.title}
DESCRIPTION:${event.description}
LOCATION:${event.location || ''}
END:VEVENT
END:VCALENDAR`;
      
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    
    setShowCalendarMenu(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header with Image */}
        {event.image && (
          <div className="relative h-64 w-full">
            <img
              src={event.image}
              alt={event.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-xl text-gray-900"></i>
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-6 sm:p-8">
          {!event.image && (
            <div className="flex justify-end mb-4">
              <button
                onClick={onClose}
                className="w-10 h-10 hover:bg-gray-100 rounded-full flex items-center justify-center cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-xl text-gray-900"></i>
              </button>
            </div>
          )}

          {/* Level Badge */}
          <div className="mb-4">
            <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-white ${getLevelColor(event.level)}`}>
              <i className={`${getLevelIcon(event.level)} mr-2`}></i>
              {event.levelName}
            </span>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
            {event.title}
          </h2>

          {/* Description */}
          <p className="text-gray-700 text-lg mb-6 leading-relaxed">
            {event.description}
          </p>

          {/* Event Details */}
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 ${getLevelColor(event.level)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <i className="ri-calendar-line text-white text-lg"></i>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-600">Date</div>
                <div className="text-base text-gray-900">{formatDateRange(event.startDate, event.endDate)}</div>
              </div>
            </div>

            {event.time && (
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 ${getLevelColor(event.level)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <i className="ri-time-line text-white text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-600">Time</div>
                  <div className="text-base text-gray-900">{event.time}</div>
                </div>
              </div>
            )}

            {event.location && (
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 ${getLevelColor(event.level)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <i className="ri-map-pin-line text-white text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-600">Location</div>
                  <div className="text-base text-gray-900">{event.location}</div>
                </div>
              </div>
            )}

            {event.isOnline && (
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 ${getLevelColor(event.level)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <i className="ri-live-line text-white text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-600">Format</div>
                  <div className="text-base text-gray-900">Online Participation Available</div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 ${getLevelColor(event.level)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <i className="ri-price-tag-3-line text-white text-lg"></i>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-600">Category</div>
                <div className="text-base text-gray-900">{event.category}</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {event.level !== 'National' && (
            <div className="flex flex-wrap gap-3 pt-6 border-t border-gray-200">
              <a
                href={event.level === 'GC' ? 'https://www.adventist.org' : event.level === 'Division' ? 'https://wad.adventist.org' : 'https://adventist.ng'}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex-1 px-6 py-3 ${getLevelColor(event.level)} text-white rounded-lg font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap text-center`}
              >
                <i className="ri-external-link-line mr-2"></i>
                View Event Page
              </a>
              
              <div className="relative">
                <button
                  onClick={() => {
                    setShowShareMenu(!showShareMenu);
                    setShowCalendarMenu(false);
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-line mr-2"></i>
                  Share
                </button>
                
                {showShareMenu && (
                  <div className="absolute bottom-full mb-2 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-48 z-10">
                    <button
                      onClick={() => handleShare('facebook')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                    >
                      <i className="ri-facebook-fill text-blue-600 text-lg"></i>
                      <span className="text-sm font-medium text-gray-700">Facebook</span>
                    </button>
                    <button
                      onClick={() => handleShare('twitter')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                    >
                      <i className="ri-twitter-x-fill text-gray-900 text-lg"></i>
                      <span className="text-sm font-medium text-gray-700">Twitter</span>
                    </button>
                    <button
                      onClick={() => handleShare('whatsapp')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                    >
                      <i className="ri-whatsapp-fill text-green-600 text-lg"></i>
                      <span className="text-sm font-medium text-gray-700">WhatsApp</span>
                    </button>
                    <button
                      onClick={() => handleShare('email')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                    >
                      <i className="ri-mail-fill text-gray-600 text-lg"></i>
                      <span className="text-sm font-medium text-gray-700">Email</span>
                    </button>
                    <div className="border-t border-gray-200 my-1"></div>
                    <button
                      onClick={() => handleShare('copy')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                    >
                      <i className={`${copySuccess ? 'ri-check-line text-green-600' : 'ri-link text-gray-600'} text-lg`}></i>
                      <span className="text-sm font-medium text-gray-700">
                        {copySuccess ? 'Link Copied!' : 'Copy Link'}
                      </span>
                    </button>
                  </div>
                )}
              </div>
              
              <div className="relative">
                <button
                  onClick={() => {
                    setShowCalendarMenu(!showCalendarMenu);
                    setShowShareMenu(false);
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line mr-2"></i>
                  Add to Calendar
                </button>
                
                {showCalendarMenu && (
                  <div className="absolute bottom-full mb-2 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-48 z-10">
                    <button
                      onClick={() => handleAddToCalendar('google')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                    >
                      <i className="ri-google-fill text-red-600 text-lg"></i>
                      <span className="text-sm font-medium text-gray-700">Google Calendar</span>
                    </button>
                    <button
                      onClick={() => handleAddToCalendar('outlook')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                    >
                      <i className="ri-microsoft-fill text-blue-600 text-lg"></i>
                      <span className="text-sm font-medium text-gray-700">Outlook</span>
                    </button>
                    <button
                      onClick={() => handleAddToCalendar('ics')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                    >
                      <i className="ri-calendar-download-line text-gray-600 text-lg"></i>
                      <span className="text-sm font-medium text-gray-700">Download .ics</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
